"use client"

import { motion, useReducedMotion } from "framer-motion"

export type SectionVariant = "default" | "modern" | "vibrant" | "minimal"

/**
 * Sistema de separadores de secção (assinatura visual do menu):
 * eyebrow em caps + pequeno acento + régua fina, seguidos do título.
 * Cada variante temática tem os seus próprios tokens.
 */
export const SECTION_STYLES: Record<
  SectionVariant,
  {
    wrapper: string
    eyebrowRow: string
    eyebrow: string
    accent: string
    rule: string
    title: string
    subtitle: string
  }
> = {
  default: {
    wrapper: "mb-8 sm:mb-10 lg:mb-12",
    eyebrowRow: "flex items-center gap-3",
    eyebrow: "text-[0.65rem] sm:text-xs font-semibold uppercase tracking-[0.22em] text-primary whitespace-nowrap",
    accent: "h-px w-8 bg-primary flex-shrink-0",
    rule: "flex-1 h-px bg-border",
    title: "text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mt-3 sm:mt-4",
    subtitle: "text-sm sm:text-base lg:text-lg text-muted-foreground mt-2 leading-relaxed max-w-2xl",
  },
  modern: {
    wrapper: "mb-8 sm:mb-10 lg:mb-12",
    eyebrowRow: "flex items-center gap-3",
    eyebrow:
      "text-[0.65rem] sm:text-xs font-semibold uppercase tracking-[0.22em] text-orange-300 whitespace-nowrap",
    accent: "h-px w-8 bg-gradient-to-r from-orange-500 to-amber-400 flex-shrink-0",
    rule: "flex-1 h-px bg-white/10",
    title: "text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-3 sm:mt-4",
    subtitle: "text-sm sm:text-base lg:text-lg text-white/60 mt-2 leading-relaxed max-w-2xl",
  },
  vibrant: {
    wrapper: "mb-8 sm:mb-10 lg:mb-12",
    eyebrowRow: "flex items-center gap-3",
    eyebrow:
      "text-[0.65rem] sm:text-xs font-bold uppercase tracking-[0.22em] text-purple-600 whitespace-nowrap",
    accent: "h-1 w-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex-shrink-0",
    rule: "flex-1 h-px bg-gray-200",
    title: "text-2xl sm:text-3xl lg:text-4xl font-black text-gray-800 mt-3 sm:mt-4",
    subtitle: "text-sm sm:text-base lg:text-lg text-gray-600 mt-2 leading-relaxed max-w-2xl",
  },
  minimal: {
    wrapper: "mb-8 sm:mb-10 lg:mb-12",
    eyebrowRow: "flex items-center gap-3",
    eyebrow:
      "font-mono text-[0.65rem] sm:text-xs uppercase tracking-[0.3em] text-foreground/70 whitespace-nowrap",
    accent: "h-0.5 w-8 bg-foreground flex-shrink-0",
    rule: "flex-1 h-px bg-foreground/20",
    title: "text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-widest text-foreground mt-3 sm:mt-4",
    subtitle: "font-mono text-xs sm:text-sm uppercase tracking-wider text-neutral-500 mt-2 max-w-2xl",
  },
}

interface SectionHeaderProps {
  eyebrow: string
  title: string
  subtitle?: string
  variant?: SectionVariant
}

export default function SectionHeader({ eyebrow, title, subtitle, variant = "default" }: SectionHeaderProps) {
  const s = SECTION_STYLES[variant]
  const reduceMotion = useReducedMotion()

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={s.wrapper}
    >
      <div className={s.eyebrowRow}>
        <span className={s.eyebrow}>{eyebrow}</span>
        <span className={s.accent} aria-hidden="true" />
        <span className={s.rule} aria-hidden="true" />
      </div>
      <h2 className={s.title}>{title}</h2>
      {subtitle && <p className={s.subtitle}>{subtitle}</p>}
    </motion.div>
  )
}
